import { useCallback, useMemo } from 'react';

import LessonProgressDashboardStats from '@/models/LessonProgressDashboardStats';
import { IconType } from '@/components/ui/custom/LessonIconContainer';
import {
  ACTIVITIES_BASE,
  BOOK_BASE,
  CHALLENGE_BASE,
  CHAPTER_BASE,
  HELP_BASE,
  ROUTES,
  SUBCHAPTER_BASE,
} from '@/utils/routes';
import { BookStyleEnum } from '@/models/Book';

type HookOptions = Pick<
  LessonProgressDashboardStats,
  | 'activitiesProgress'
  | 'bookProgress'
  | 'hasAccessedTool'
  | 'hasWatchedInstruction'
  | 'challengeProgress'
> & {
  slugCourseName: string;
  lessonId: number;
  klassId?: number;
};

export function useLessonProgressStats(options: HookOptions) {
  const {
    activitiesProgress,
    bookProgress,
    hasAccessedTool,
    hasWatchedInstruction,
    challengeProgress,
    slugCourseName,
    lessonId,
    klassId,
  } = options;

  const hasDoneActivities = useMemo(() => {
    return activitiesProgress.some(activity => activity.hasDone);
  }, [activitiesProgress]);

  const hasDoneAccessInfo = useMemo(() => {
    return hasAccessedTool && hasWatchedInstruction;
  }, [hasAccessedTool, hasWatchedInstruction]);

  type Stats = {
    id: IconType;
    done: boolean;
    to: string;
  };

  const basePath = ROUTES.COURSES.LESSON.INFO(
    slugCourseName,
    lessonId,
    klassId,
  );

  const bookRoute = useCallback(
    (basePath: string, bookStyle: BookStyleEnum) => {
      if (bookStyle === BookStyleEnum.APRESENTATION) {
        return `${basePath}${BOOK_BASE}${CHAPTER_BASE}/1${SUBCHAPTER_BASE}/1`;
      }

      return `${basePath}${BOOK_BASE}${CHAPTER_BASE}/1`;
    },
    [],
  );

  const stats = useMemo<Stats[]>(() => {
    const initial: Stats[] = [];

    if (hasAccessedTool !== null || hasWatchedInstruction !== null) {
      initial.push({
        id: 'access',
        done: hasDoneAccessInfo,
        to: `${basePath}${HELP_BASE}`,
      });
    }

    if (bookProgress) {
      initial.push({
        id: 'studentBook',
        done: bookProgress.hasCompleted,
        to: bookRoute(basePath, bookProgress.bookStyle),
      });
    }

    if (!!activitiesProgress.length) {
      initial.push({
        id: 'activity',
        done: hasDoneActivities,
        to: `${basePath}${ACTIVITIES_BASE}`,
      });
    }

    if (challengeProgress) {
      initial.push({
        id: 'challenge',
        done: challengeProgress.hasDone,
        to: `${basePath}${CHALLENGE_BASE}`,
      });
    }

    return initial;
  }, [
    hasDoneAccessInfo,
    hasDoneActivities,
    bookProgress,
    challengeProgress,
    basePath,
    bookRoute,
    activitiesProgress,
    hasAccessedTool,
    hasWatchedInstruction,
  ]);

  return { stats };
}
